<template>
    <div class="gs-checkout-rule-editor">
        <section class="gs-checkout-default-card">
            <div class="gs-checkout-default-card__copy">
                <small>Default cart abandonment</small>
                <h4>Set the normal recovery integrations for this product.</h4>
                <p>Price points can use this default recovery setup, or you can modify a single price point when it needs different automation.</p>
            </div>
            <div class="gs-checkout-default-card__body">
                <div v-if="defaultIntegrations.length" class="gs-checkout-integration-list">
                    <article v-for="(integration, index) in defaultIntegrations" :key="index">
                        <strong>{{ integrationLabel(integration) }}</strong>
                        <span>{{ integrationDetails(integration) }}</span>
                    </article>
                </div>
                <div v-else class="gs-checkout-empty-note">
                    No default cart abandonment integration is configured yet. Modify a price point, add an integration, then apply it as the default.
                </div>
            </div>
        </section>

        <section class="gs-checkout-price-rules">
            <div class="gs-checkout-price-rules__head">
                <div>
                    <h4>Price point recovery rules</h4>
                    <p>Keep price points on the default unless a specific offer needs different cart recovery behavior.</p>
                </div>
            </div>

            <article
                v-for="(pricePoint, index) in localModel.pricing_points"
                :key="pricePoint.id || index"
                class="gs-checkout-price-rule"
                :class="{ 'is-custom': !isUsingDefaultCart(pricePoint) }"
            >
                <header class="gs-checkout-price-rule__head">
                    <div>
                        <strong>{{ pricePoint.name }}</strong>
                        <span>{{ cartSummary(pricePoint) }}</span>
                    </div>
                    <div class="gs-checkout-price-rule__actions">
                        <em :class="{ 'is-custom': !isUsingDefaultCart(pricePoint) }">
                            {{ isUsingDefaultCart(pricePoint) ? 'Default' : 'Custom' }}
                        </em>
                        <label>
                            <span>Apply default</span>
                            <gd-toggle
                                aria-label="Apply the default cart-abandonment settings"
                                :value="isUsingDefaultCart(pricePoint)"
                                @input="toggleCartDefault(pricePoint, $event)"
                            />
                        </label>
                        <button type="button" @click="toggleCartEditor(index)">
                            {{ openCartIndex === index ? 'Collapse' : 'Edit settings' }}
                        </button>
                    </div>
                </header>

                <div v-if="openCartIndex === index && !isUsingDefaultCart(pricePoint)" class="gs-checkout-price-rule__body">
                    <div class="gs-checkout-rule-toolbar">
                        <button
                            v-if="canAddIntegrations"
                            type="button"
                            class="gs-checkout-editor__primary"
                            @click="showAbandonCartIntegrationBox(index)"
                        >
                            <plus-icon class="h-4 w-4 fill-current"/>
                            {{ $t('frontend_product_step5_add_new_integration') }}
                        </button>
                        <button type="button" class="gs-checkout-secondary-button" @click="setCartAsDefault(pricePoint)">
                            Apply as default to all price points
                        </button>
                        <button
                            v-if="!canAddIntegrations"
                            type="button"
                            class="gs-checkout-secondary-button"
                            @click="openIntegrationModal"
                        >
                            <plus-icon class="h-4 w-4 fill-current"/>
                            {{ $t('frontend_groovesell_products_step5_btn_integrations_modal') }}
                        </button>
                    </div>

                    <div v-if="pricePoint.abandon_cart_integrations && pricePoint.abandon_cart_integrations.length" class="gs-checkout-integration-list">
                        <article
                            v-for="(integration, integrationIndex) in pricePoint.abandon_cart_integrations"
                            :key="integrationIndex"
                        >
                            <div>
                                <strong>{{ integrationLabel(integration) }}</strong>
                                <span>{{ integrationDetails(integration) }}</span>
                            </div>
                            <button type="button" @click="openConfirmModal(index, integrationIndex)">
                                <trash-icon class="h-4 w-4 fill-current"/>
                                Delete
                            </button>
                        </article>
                    </div>
                    <div v-else class="gs-checkout-empty-note">
                        {{ canAddIntegrations ? $t('frontend_product_step5_no_integrations') : $t('frontend_product_step5_no_integrations_added') }}
                    </div>
                </div>
            </article>
        </section>

        <gs-alert-dialog
            v-model="showDeleteCartIntegrationConfirm"
            title="Delete cart integration?"
            :copy="$t('frontend_non_revertable')"
            confirm-label="Delete"
            cancel-label="Cancel"
            @confirm="deleteAbandonCartIntegration"
        />
    </div>
</template>

<script>
import TrashIcon from '@/assets/images/icons/TrashIcon.svg';
import PlusIcon from '@/assets/images/icons/PlusIcon.svg';

export default {
    components: {
        TrashIcon,
        PlusIcon,
    },
    props: {
        model: {
            required: true
        },
        validation: {
            required: true
        },
        openIntegrationModal: {
            type: Function,
            required: true
        },
        pluginSiteData: {
            type: Object,
            required: true
        },
        availableIntegrations: {
            type: Array,
            required: true
        },
        showAbandonCartIntegrationBox: {
            type: Function,
            required: true
        },
    },
    data() {
        const firstWithIntegrations = (this.model.pricing_points || []).find((pricePoint) => {
            return pricePoint.abandon_cart_integrations && pricePoint.abandon_cart_integrations.length;
        });

        return {
            localModel: this.model,
            openCartIndex: null,
            pricePointIndex: null,
            abandonCartIntegrationIndex: null,
            showDeleteCartIntegrationConfirm: false,
            defaultIntegrations: this.cloneIntegrations(
                (this.model.checkout_page_options && this.model.checkout_page_options.cart_abandonment_default_integrations) ||
                (firstWithIntegrations && firstWithIntegrations.abandon_cart_integrations) ||
                []
            ),
        }
    },
    computed: {
        canAddIntegrations() {
            return this.pluginSiteData.integrations.length > 0 && this.availableIntegrations.length > 0;
        },
    },
    mounted() {
        this.ensureCartDefaults();
    },
    watch: {
        localModel: {
            handler() {
                this.$emit('updateModel', this.localModel);
            },
            deep: true
        }
    },
    methods: {
        cloneIntegrations(integrations) {
            return JSON.parse(JSON.stringify(integrations || []));
        },
        ensureCartDefaults() {
            if (!this.localModel.checkout_page_options) {
                this.$set(this.localModel, 'checkout_page_options', {});
            }
            if (!this.localModel.checkout_page_options.cart_abandonment_default_integrations) {
                this.$set(this.localModel.checkout_page_options, 'cart_abandonment_default_integrations', this.cloneIntegrations(this.defaultIntegrations));
            }
            this.localModel.pricing_points.forEach((pricePoint) => {
                if (pricePoint.cart_abandonment_use_default == null) {
                    this.$set(pricePoint, 'cart_abandonment_use_default', this.cartMatchesDefault(pricePoint));
                }
                if (this.isUsingDefaultCart(pricePoint)) {
                    this.applyDefaultCart(pricePoint);
                }
            });
        },
        cartMatchesDefault(pricePoint) {
            return JSON.stringify(pricePoint.abandon_cart_integrations || []) === JSON.stringify(this.defaultIntegrations || []);
        },
        isUsingDefaultCart(pricePoint) {
            return pricePoint.cart_abandonment_use_default !== false;
        },
        applyDefaultCart(pricePoint) {
            this.$set(pricePoint, 'cart_abandonment_use_default', true);
            this.$set(pricePoint, 'abandon_cart_integrations', this.cloneIntegrations(this.defaultIntegrations));
        },
        syncDefaultCartToInherited() {
            this.$set(this.localModel.checkout_page_options, 'cart_abandonment_default_integrations', this.cloneIntegrations(this.defaultIntegrations));
            this.localModel.pricing_points.forEach((pricePoint) => {
                if (this.isUsingDefaultCart(pricePoint)) {
                    this.applyDefaultCart(pricePoint);
                }
            });
        },
        toggleCartDefault(pricePoint, value) {
            this.$set(pricePoint, 'cart_abandonment_use_default', !!value);
            if (value) {
                this.applyDefaultCart(pricePoint);
            }
        },
        toggleCartEditor(index) {
            const pricePoint = this.localModel.pricing_points[index];
            if (this.isUsingDefaultCart(pricePoint)) {
                this.toggleCartDefault(pricePoint, false);
            }
            this.openCartIndex = this.openCartIndex === index ? null : index;
        },
        setCartAsDefault(pricePoint) {
            this.defaultIntegrations = this.cloneIntegrations(pricePoint.abandon_cart_integrations || []);
            this.syncDefaultCartToInherited();
        },
        cartSummary(pricePoint) {
            if (this.isUsingDefaultCart(pricePoint)) return `${this.defaultIntegrations.length} default ${this.defaultIntegrations.length === 1 ? 'integration' : 'integrations'}`;
            const count = (pricePoint.abandon_cart_integrations || []).length;
            return `${count} custom ${count === 1 ? 'integration' : 'integrations'}`;
        },
        integrationLabel(integration) {
            if (!integration || !integration.platform) return 'Integration';
            return integration.platform.charAt(0).toUpperCase() + integration.platform.slice(1);
        },
        integrationDetails(integration) {
            if (!integration || !integration.config || !integration.config.length) return 'No configuration details saved';
            return integration.config.map((item) => `${item.label}: ${item.name}`).join(' • ');
        },
        openConfirmModal(index, abandonCartIntegrationIndex) {
            this.pricePointIndex = index;
            this.abandonCartIntegrationIndex = abandonCartIntegrationIndex;
            this.showDeleteCartIntegrationConfirm = true;
        },
        async deleteAbandonCartIntegration() {
            this.localModel.pricing_points[this.pricePointIndex]
                .abandon_cart_integrations.splice(this.abandonCartIntegrationIndex, 1);
        }
    },
}
</script>
